import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Action} from '@ngrx/store';
import {Observable, of as observableOf, Subject} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';

import {NotificationService} from '../../../../../core/services';
import {deleteDibujos} from '../dibujos/dibujos.actions';
import * as adjuntosActions from './adjuntos.actions';
import {LocalStorageService} from "../../../../../core/services";
import {AUTH_ROLE_ID} from "../../../../../core/models";
import {QueryResultApi} from "../../../../../shared/models";
import {Adjunto} from "../../../../../chacra-core/models/adjunto";
import {AdjuntosService} from "../../../../../chacra-core/services/adjuntos.service";

@Injectable()
export class AdjuntosEffects {

  // load
  LoadRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<adjuntosActions.AdjuntosLoadRequestAction>(
      adjuntosActions.AdjuntoActionTypes.LOAD_REQUEST
    ),
    switchMap((action) => {

      const sub: Subject<Action> = new Subject<Action>();

      const rolId = this.localStorageService.getItem(AUTH_ROLE_ID);
      let params: any = { rolId };

      const requestObservers = [];
      requestObservers.push();

      this.adjuntosService.get(params).subscribe((result: QueryResultApi<Adjunto>) => {
          const action = new adjuntosActions.AdjuntosLoadSuccessAction({
            items: result.items
          });
          sub.next(action);
          sub.complete();
        },
        (error => {
          const action = new adjuntosActions.AdjuntosLoadFailureAction({error});
          sub.next(action);
          sub.complete();
        })
      );
      return sub;
    })
  ));

  // add
  AdjuntosAddRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<adjuntosActions.AdjuntosAddRequestAction>(adjuntosActions.AdjuntoActionTypes.ADD_REQUEST),
    map((action: adjuntosActions.AdjuntosAddRequestAction) => action.payload),
    switchMap(payload => {
      return this.adjuntosService.add(payload.item).pipe(
        map(addResult => {
          return new adjuntosActions.AdjuntosAddSuccessAction({
            item: {...addResult},
            dibujoId: payload.dibujoId
          });
        }),
        catchError(error =>
          observableOf(
            new adjuntosActions.AdjuntosAddFailureAction({
              error
            })
          )
        )
      );
    })
  ));

  // add many
  AdjuntosAddManyRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<adjuntosActions.AdjuntosAddManyRequestAction>(adjuntosActions.AdjuntoActionTypes.ADD_MANY_REQUEST),
    map((action: adjuntosActions.AdjuntosAddManyRequestAction) => action.payload),
    switchMap(payload => {

      const itemsSend = payload.items.map(a => {
        return {
          ...a,
          adjuntoRubro: null,
        };
      });

      const sub: Subject<Action> = new Subject<Action>();
      this.adjuntosService.addMany(itemsSend).subscribe(
        addManyResult => {

          for (const err of addManyResult.errors) {
            this.notificationService.error(err);
          }

          const items = addManyResult.items.map(a => {
            return {
              ...a,
            };
          });
          const actionSuccess = new adjuntosActions.AdjuntosAddManySuccessAction({items});
          sub.next(actionSuccess);
          sub.complete();
        },
        (error) => {
          const action = new adjuntosActions.AdjuntosAddManyFailureAction({error});
          sub.next(action);
          sub.complete();
        }
      );
      return sub;
    })
  ));

  // add callback
  AdjuntosAddRequestWithCallbackEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<adjuntosActions.AdjuntosAddRequestWithCallbackAction>(adjuntosActions.AdjuntoActionTypes.ADD_REQUEST_WITH_CALLBACK),
    switchMap((action: adjuntosActions.AdjuntosAddRequestWithCallbackAction) => {
      const sub: Subject<Action> = new Subject<Action>();
      this.adjuntosService.add(action.payload.item).subscribe(addResult => {
          const actionSuccess = new adjuntosActions.AdjuntosAddSuccessAction({
            item: {...addResult},
            dibujoId: action.payload.dibujoId
          });

          const data = {...action.dataCallback};
          if (Array.isArray(data.item)){
            data.item = data.item.map(it => {
              return {
                ...it,
                [action.adjuntoKey]: addResult.adjuntoId
              };
            });
          }
          else{
            data.item = {
              ...data.item,
              [action.adjuntoKey]: addResult.adjuntoId
            };
          }
          const actionCallback = action.actionCallback(data);

          sub.next(actionSuccess);
          sub.next(actionCallback);
          sub.complete();
        },
        (error) => {
          const action = new adjuntosActions.AdjuntosAddFailureAction({error});
          sub.next(action);
          sub.complete();
        });
      return sub;
    })
  ));

  // add many callback
  AdjuntosAddManyRequestWithCallbackEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<adjuntosActions.AdjuntosAddManyRequestWithCallbackAction>(adjuntosActions.AdjuntoActionTypes.ADD_MANY_REQUEST_WITH_CALLBACK),
    switchMap((action: adjuntosActions.AdjuntosAddManyRequestWithCallbackAction) => {
      const sub: Subject<Action> = new Subject<Action>();
      if(action.payload.items.length > 0){
        const itemsSend = action.payload.items.map(a => {
          return {
            ...a,
          };
        });

        this.adjuntosService.addMany(itemsSend).subscribe(addManyResult => {

            for (const err of addManyResult.errors) {
              this.notificationService.error(err);
            }

            const items = addManyResult.items.map(a => {
              return {
                ...a,
              };
            });
            const actionSuccess = new adjuntosActions.AdjuntosAddManySuccessAction({items});

            let adjuntoKeys = action.adjuntoKeys.map(ak => {
              let val = null;
              let it = items.find(it => it.adjuntoTipo === ak.tipo);
              if(it)
                val = it.adjuntoId;
              return {
               key: ak.key,
               val
              }
            });

            let itemKeyAd: any = {};
            for(let k of adjuntoKeys){
              itemKeyAd[k.key] = k.val;
            }

            const data = {...action.dataCallback};
            if (Array.isArray(data.item)){
              data.item = data.item.map(it => {
                return {
                  ...it,
                  ...itemKeyAd
                };
              });
            }
            else{
              data.item = {
                ...data.item,
                ...itemKeyAd
              };
            }
            const actionCallback = action.actionCallback(data);

            sub.next(actionSuccess);
            sub.next(actionCallback);
            sub.complete();
          },
          (error) => {
            const action = new adjuntosActions.AdjuntosAddManyFailureAction({error});
            sub.next(action);
            sub.complete();
          });
      }
      return sub;
    })
  ));

  // add success
  AdjuntosAddSuccessEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<adjuntosActions.AdjuntosAddSuccessAction>(adjuntosActions.AdjuntoActionTypes.ADD_SUCCESS),
    switchMap((action: adjuntosActions.AdjuntosAddSuccessAction) => {
      return observableOf(
        deleteDibujos({ids: [action.payload.dibujoId]})
      );
    })
  ));

  // delete
  AdjuntosDeleteRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<adjuntosActions.AdjuntosDeleteRequestAction>(adjuntosActions.AdjuntoActionTypes.DELETE_REQUEST),
    map((action: adjuntosActions.AdjuntosDeleteRequestAction) => action.payload.item),
    switchMap(item => {
      return this.adjuntosService.delete(item.adjuntoId).pipe(
        map(deleteResult => {
          return new adjuntosActions.AdjuntosDeleteSuccessAction({
            item
          });
        }),
        catchError(error =>
          observableOf(
            new adjuntosActions.AdjuntosDeleteFailureAction({
              error
            })
          )
        )
      );
    })
  ));

  // error
  AdjuntosFailureEffect$: Observable<any> = createEffect(() => this.actions$.pipe(
    ofType(
      adjuntosActions.AdjuntoActionTypes.LOAD_FAILURE,
      adjuntosActions.AdjuntoActionTypes.ADD_FAILURE,
      adjuntosActions.AdjuntoActionTypes.ADD_MANY_FAILURE,
      adjuntosActions.AdjuntoActionTypes.DELETE_FAILURE,
    ),
    map(
      (
        action:
          | adjuntosActions.AdjuntosLoadFailureAction
          | adjuntosActions.AdjuntosAddFailureAction
          | adjuntosActions.AdjuntosAddManyFailureAction
          | adjuntosActions.AdjuntosDeleteFailureAction
      ) => action.payload.error
    ),
    tap((error: string) => this.notificationService.error(error))
  ), {dispatch: false});

  constructor(
    private notificationService: NotificationService,
    private adjuntosService: AdjuntosService,
    private actions$: Actions,
    protected localStorageService: LocalStorageService,
  ) {
  }
}
